import { useMeasuredData } from "@/components/worksheet/apis";
import { OneDayMeasuredData } from "@/components/worksheet/commons/measured_data";
import { ReportDailyResultImg } from "@/pages/common/report/WebReport2/commons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { SectionLayout } from ".";
import QualityCriterionHelpDialog from "../commons/dialogs/QualityCriterionHelpDialog";
import QuantityCriterionHelpDialog from "../commons/dialogs/QuantityCriterionHelpDialog";
import RhythmCriterionHelpDialog from "../commons/dialogs/RhythmCriterionHelpDialog";
import MeasuredDataSection from "../commons/MeasuredDataSection";
import { SleepSummary2 } from "../commons/sleep_summaries_2";

interface SleepConditionSectionProps {
  uuid?: string;
  sleep_quantity?: SleepSummary2["sleep_condition"]["sleep_quantity"];
  sleep_quality?: SleepSummary2["sleep_condition"]["sleep_quality"];
  sleep_rhythm?: SleepSummary2["sleep_condition"]["sleep_rhythm"];
}
/**
 * 睡眠の判定詳細
 *
 * @param props {@link SleepConditionSectionProps}
 * @returns 睡眠の判定詳細
 */
export default function SleepConditionSection({
  uuid,
  sleep_quantity,
  sleep_quality,
  sleep_rhythm,
}: SleepConditionSectionProps) {
  const measuredDataApi = useMeasuredData();
  const [allDaysMeasuredData, setAllDaysMeasuredData] = useState<
    OneDayMeasuredData[]
  >([]);
  useEffect(() => {
    if (uuid == null) return;
    (async () => {
      const res = await measuredDataApi.get(uuid);
      setAllDaysMeasuredData(res.data);
    })();
  }, [uuid, measuredDataApi]);

  if (!uuid) {
    return <></>;
  }

  const measuredData = allDaysMeasuredData.map(
    (oneDay) => oneDay.measured_data
  );
  return (
    <SectionLayout title="睡眠の判定詳細">
      {measuredData && (
        <Paper
          variant="outlined"
          sx={{ p: "16px 24px", gap: 2, alignItems: "end" }}
          component={Stack}
        >
          <MeasuredDataSection
            measuredData={measuredData}
            paddingLeft="280px"
          />
        </Paper>
      )}
      {sleep_quantity && (
        <SleepConditionSectionAccordion
          summaryTitle="睡眠の長さ"
          result={sleep_quantity.result}
          help={
            <QuantityCriterionHelpDialog
              recommendedSleepTimeLow={
                sleep_quantity.recommended_sleep_time_low
              }
              recommendedSleepTimeHigh={
                sleep_quantity.recommended_sleep_time_high
              }
              appropriateSleepTimeLow={
                sleep_quantity.appropriate_sleep_time_low
              }
              appropriateSleepTimeHigh={
                sleep_quantity.appropriate_sleep_time_high
              }
              ageRange={sleep_quantity.age_range}
            />
          }
        >
          <SleepQuantityDetail sleep_quantity={sleep_quantity} />
        </SleepConditionSectionAccordion>
      )}
      {sleep_quality && (
        <SleepConditionSectionAccordion
          summaryTitle="睡眠の質"
          result={sleep_quality.result}
          help={<QualityCriterionHelpDialog />}
        >
          <SleepQualityDetail sleep_quality={sleep_quality} />
        </SleepConditionSectionAccordion>
      )}
      {sleep_rhythm && (
        <SleepConditionSectionAccordion
          summaryTitle="睡眠のリズム"
          result={sleep_rhythm.result}
          help={<RhythmCriterionHelpDialog />}
        >
          <SleepRhythmDetail sleep_rhythm={sleep_rhythm} />
        </SleepConditionSectionAccordion>
      )}
    </SectionLayout>
  );
}

interface SleepConditionSectionAccordionProps {
  summaryTitle: string;
  result: string;
  children?: React.ReactNode;
  help?: React.ReactNode;
}

const BADGE_BACKGROUND_COLOR: Record<string, string> = {
  良好: "primary.main",
  注意: "#FFB10E",
  警戒: "error.main",
} as const;

function SleepConditionSectionAccordion(
  props: SleepConditionSectionAccordionProps
) {
  const resultBatchColor = BADGE_BACKGROUND_COLOR[props.result] ?? "#A3A3A3";
  const [open, setOpen] = useState(true);
  return (
    <Accordion
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 0px 0px 1px #E0E0E0",
        marginTop: "16px",
        ":before": {
          // before の height を消さないと折りたたんだ際によく分からないラインが出現する
          height: 0,
        },
      }}
      expanded={open}
      onChange={() => {
        setOpen((open) => !open);
      }}
    >
      <AccordionSummary
        sx={{
          flexDirection: "row-reverse",
          // open が false の時(アコーディオンが閉じている時)に borderBottom の線が出現するのを防ぐ
          borderBottom: `${open ? "1px" : "0"} solid #E0E0E0`,
          minHeight: 0,
          "&.Mui-expanded": {
            minHeight: 0,
          },
          "& .MuiAccordionSummary-content": {
            padding: "16px",
            margin: 0,
            "&.Mui-expanded": {
              margin: 0,
            },
          },
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <Typography>{props.summaryTitle}</Typography>
            {props.help ?? (
              <HelpOutlineIcon
                sx={{
                  height: "16px",
                  width: "16px",
                  color: "#A3A3A3",
                  marginLeft: "4px",
                }}
              />
            )}
          </Stack>
          <Stack
            sx={{
              backgroundColor: resultBatchColor,
              borderRadius: "30px",
              paddingX: "8px",
              paddingY: "2px",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "13px",
                color: "white",
                paddingX: "8px",
              }}
            >
              {props.result}
            </Typography>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "20px" }}>
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
}

interface SleepQuantityDetailProps {
  sleep_quantity: SleepSummary2["sleep_condition"]["sleep_quantity"];
}

function SleepQuantityDetail(props: SleepQuantityDetailProps) {
  const recommendedLow = props.sleep_quantity.recommended_sleep_time_low;
  const recommendedHigh = props.sleep_quantity.recommended_sleep_time_high;
  const dayCount = props.sleep_quantity.each_day_sleep.length;
  const ageRangeText = `※${props.sleep_quantity.age_range}の場合`;
  return (
    <>
      {props.sleep_quantity.improvement_comment && (
        <Typography>{props.sleep_quantity.improvement_comment}</Typography>
      )}
      <TableContainer
        sx={{
          borderRadius: "0px",
          border: "solid #E0E0E0",
          borderWidth: "1px 1px 0 1px",
          marginTop: "24px",
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "primary.dark",
                color: "white",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: "160px", paddingX: "0px" }}>
                <Typography sx={{ paddingLeft: "16px" }}>項目</Typography>
              </TableCell>
              <TableCell sx={{ width: "120px", paddingX: "0px" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    borderLeft: "2px solid #FFFFFF1F",
                    paddingLeft: "16px",
                  }}
                >
                  推奨範囲
                </Typography>
              </TableCell>
              {Array.from({ length: dayCount }).map((_, i) => (
                <TableCell key={i} sx={{ paddingLeft: "0px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      borderLeft: "2px solid #FFFFFF1F",
                      paddingLeft: "16px",
                    }}
                  >
                    {i + 1}日目
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ borderBottom: "3px double #E0E0E0" }}>
              <TableCell sx={{ width: "160px" }}>睡眠時間</TableCell>
              <TableCell
                sx={{
                  width: "120px",
                  borderLeft: "1px dashed #E0E0E0",
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                {recommendedLow}～{recommendedHigh}時間<sup>※</sup>
              </TableCell>
              {props.sleep_quantity.each_day_sleep.map((d, i) => (
                <TableCell
                  key={i}
                  sx={{ color: d.is_warning ? "error.main" : "" }}
                >
                  {d.sleep_time || "-"}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: "160px" }}>日別判定</TableCell>
              <TableCell
                sx={{ width: "120px", borderRight: "1px solid #E0E0E0" }}
              ></TableCell>
              {props.sleep_quantity.each_day_sleep.map((d, i) => (
                <TableCell key={i}>
                  {d.result ? <ReportDailyResultImg type={d.result} /> : "-"}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography
        sx={{ fontSize: "12px", color: "text.secondary", marginTop: "8px" }}
      >
        {ageRangeText}
      </Typography>
    </>
  );
}

interface SleepQualityDetailProps {
  sleep_quality: SleepSummary2["sleep_condition"]["sleep_quality"];
}

function SleepQualityDetail(props: SleepQualityDetailProps) {
  const dayCount = props.sleep_quality.each_day_sleep.length;
  return (
    <>
      {props.sleep_quality.improvement_comment && (
        <Typography>{props.sleep_quality.improvement_comment}</Typography>
      )}
      <TableContainer
        sx={{
          borderRadius: "0px",
          border: "solid #E0E0E0",
          borderWidth: "1px 1px 0 1px",
          marginTop: "24px",
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "primary.dark",
                color: "white",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: "160px", paddingX: "0px" }}>
                <Typography sx={{ paddingLeft: "16px" }}>項目</Typography>
              </TableCell>
              <TableCell sx={{ width: "120px", paddingX: "0px" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    borderLeft: "2px solid #FFFFFF1F",
                    paddingLeft: "16px",
                  }}
                >
                  推奨範囲
                </Typography>
              </TableCell>
              {Array.from({ length: dayCount }).map((_, i) => (
                <TableCell key={i} sx={{ paddingLeft: "0px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      borderLeft: "2px solid #FFFFFF1F",
                      paddingLeft: "16px",
                    }}
                  >
                    {i + 1}日目
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ borderBottom: "1px solid #E0E0E0" }}>
              <TableCell sx={{ width: "160px" }}>睡眠効率</TableCell>
              <TableCell
                sx={{
                  width: "120px",
                  borderLeft: "1px dashed #E0E0E0",
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                85％以上
              </TableCell>
              {props.sleep_quality.each_day_sleep.map((d, i) => (
                <TableCell
                  key={i}
                  sx={{ color: d.is_warning_efficiency ? "error.main" : "" }}
                >
                  {d.sleep_efficiency || "-"}
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ borderBottom: "1px solid #E0E0E0" }}>
              <TableCell sx={{ width: "160px" }}>入眠潜時</TableCell>
              <TableCell
                sx={{
                  width: "120px",
                  borderLeft: "1px dashed #E0E0E0",
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                30分未満
              </TableCell>
              {props.sleep_quality.each_day_sleep.map((d, i) => (
                <TableCell
                  key={i}
                  sx={{ color: d.is_warning_latency ? "error.main" : "" }}
                >
                  {d.sleep_latency || "-"}
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ borderBottom: "3px double #E0E0E0" }}>
              <TableCell sx={{ width: "160px" }}>中途覚醒時間</TableCell>
              <TableCell
                sx={{
                  width: "120px",
                  borderLeft: "1px dashed #E0E0E0",
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                1時間未満
              </TableCell>
              {props.sleep_quality.each_day_sleep.map((d, i) => (
                <TableCell
                  key={i}
                  sx={{
                    color: d.is_warning_awakening_time ? "error.main" : "",
                  }}
                >
                  {d.awakening_time || "-"}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: "160px" }}>日別判定</TableCell>
              <TableCell
                sx={{ width: "120px", borderRight: "1px solid #E0E0E0" }}
              ></TableCell>
              {props.sleep_quality.each_day_sleep.map((d, i) => (
                <TableCell key={i}>
                  {d.result ? <ReportDailyResultImg type={d.result} /> : "-"}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

interface SleepRhythmDetailProps {
  sleep_rhythm: SleepSummary2["sleep_condition"]["sleep_rhythm"];
}

function SleepRhythmDetail(props: SleepRhythmDetailProps) {
  const dayCount = props.sleep_rhythm.each_day_sleep.length;
  return (
    <>
      {props.sleep_rhythm.improvement_comment && (
        <Typography>{props.sleep_rhythm.improvement_comment}</Typography>
      )}
      <TableContainer
        sx={{
          borderRadius: "0px",
          border: "solid #E0E0E0",
          borderWidth: "1px 1px 0 1px",
          marginTop: "24px",
          width: "35%",
          minWidth: "475px",
        }}
      >
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "primary.dark",
                color: "white",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: "280px", paddingX: "0px" }}>
                <Typography sx={{ paddingLeft: "16px" }}>項目</Typography>
              </TableCell>
              <TableCell sx={{ paddingX: "0px" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    borderLeft: "2px solid #FFFFFF1F",
                    paddingLeft: "16px",
                  }}
                >
                  測定結果
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: "160px" }}>起床時刻中央値</TableCell>
              <TableCell
                sx={{
                  width: "120px",
                  borderLeft: "1px solid #E0E0E0",
                }}
              >
                {props.sleep_rhythm.median_of_wake_up_time || "-"}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer
        sx={{
          borderRadius: "0px",
          border: "solid #E0E0E0",
          borderWidth: "1px 1px 0 1px",
          marginTop: "24px",
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: "primary.dark",
                color: "white",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: "160px", paddingX: "0px" }}>
                <Typography sx={{ paddingLeft: "16px" }}>項目</Typography>
              </TableCell>
              <TableCell sx={{ width: "120px", paddingX: "0px" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    borderLeft: "2px solid #FFFFFF1F",
                    paddingLeft: "16px",
                  }}
                >
                  推奨範囲
                </Typography>
              </TableCell>
              {Array.from({ length: dayCount }).map((_, i) => (
                <TableCell key={i} sx={{ paddingLeft: "0px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      borderLeft: "2px solid #FFFFFF1F",
                      paddingLeft: "16px",
                    }}
                  >
                    {i + 1}日目
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ borderBottom: "3px double #E0E0E0" }}>
              <TableCell sx={{ width: "160px" }}>起床時刻のズレ</TableCell>
              <TableCell
                sx={{
                  width: "120px",
                  borderLeft: "1px dashed #E0E0E0",
                  borderRight: "1px solid #E0E0E0",
                }}
              >
                ±2時間未満
              </TableCell>
              {props.sleep_rhythm.each_day_sleep.map((d, i) => (
                <TableCell
                  key={i}
                  sx={{
                    color: d.is_warning ? "error.main" : "",
                  }}
                >
                  {d.social_jet_lag || "-"}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: "160px" }}>日別判定</TableCell>
              <TableCell
                sx={{ width: "120px", borderRight: "1px solid #E0E0E0" }}
              ></TableCell>
              {props.sleep_rhythm.each_day_sleep.map((d, i) => (
                <TableCell key={i}>
                  {d.result ? <ReportDailyResultImg type={d.result} /> : "-"}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
